import { Email, Person } from "@mui/icons-material";
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  Link as LinkBase,
} from "@mui/material";
import { StrugglingFlagChip } from "components/StrugglingFlag/StrugglingFlagChip";
import CustomTable from "components/Table/CustomTable";
import { UserWithPet } from "components/VetStore/CoachPetAvatar";
import { SortOrder, TABLE_ACTIONS, TAG_COLOR } from "helpers/constants";
import { useStrugglingLearnerRoster } from "hooks/useStrugglingLearnerRoster";
import { FC, useMemo, useState } from "react";
import { ROLES, User } from "types/user";
import { Link } from "react-router-dom";
import { filterUserByNameOrEmail } from "../../userTableFilters";

type UserTableProps = {
  users: User[];
  role?: string;
  handleActionClick: (action: TABLE_ACTIONS, id: string) => void;
};

export const UserTable: FC<UserTableProps> = ({
  users,
  role = "all",
  handleActionClick,
}) => {
  const [selectedOrganization, setSelectedOrganization] =
    useState<string>("all");
  const [strugglingFilter, setStrugglingFilter] = useState<string>("all");
  const rosterOrgId =
    role === ROLES.STUDENT && selectedOrganization !== "all"
      ? selectedOrganization
      : undefined;
  const { strugglingByStudentId } = useStrugglingLearnerRoster(rosterOrgId);
  const showStrugglingColumn = Boolean(rosterOrgId);

  const organizations = useMemo(() => {
    const orgs = new Map<string, string>();
    users.forEach((user) => {
      user.organization?.forEach((org) => {
        if (org.org_id) orgs.set(org.org_id, org.name ?? org.org_id);
      });
    });
    return Array.from(orgs, ([org_id, name]) => ({ org_id, name })).sort(
      (a, b) => a.name.localeCompare(b.name),
    );
  }, [users]);

  const filterUsers = useMemo(() => {
    return users.filter((user) => {
      let roleMatch = true;
      let orgMatch = true;

      if (role !== "all") {
        roleMatch = user.role === role;
      }

      if (selectedOrganization !== "all") {
        orgMatch =
          user.organization?.some((org) => org.org_id === selectedOrganization) ||
          false;
      }

      let strugglingMatch = true;
      if (showStrugglingColumn && strugglingFilter !== "all") {
        const summary = strugglingByStudentId?.get(user.user_id || "");
        if (strugglingFilter === "struggling") {
          strugglingMatch = Boolean(summary?.isStruggling);
        } else if (strugglingFilter === "fine") {
          strugglingMatch = !summary?.isStruggling;
        }
      }

      return roleMatch && orgMatch && strugglingMatch;
    });
  }, [
    users,
    role,
    selectedOrganization,
    strugglingFilter,
    showStrugglingColumn,
    strugglingByStudentId,
  ]);

  const handleOrganizationChange = (event: any) => {
    setSelectedOrganization(event.target.value);
  };

  return (
    <Box>
      <Box mb={2}>
        <Stack direction="row" spacing={2} alignItems={"center"}>
          <Box>Filter by:</Box>
          <FormControl variant="outlined" sx={{ minWidth: 200, mr: 2 }}>
            <InputLabel id="organization-filter-label">Organization</InputLabel>
            <Select
              labelId="organization-filter-label"
              id="organization-filter"
              value={selectedOrganization}
              onChange={handleOrganizationChange}
              label="Organization"
              size="small"
            >
              <MenuItem value="all">All Organizations</MenuItem>
              {organizations.map((org) => (
                <MenuItem key={org.org_id} value={org.org_id}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {showStrugglingColumn && (
            <FormControl variant="outlined" sx={{ minWidth: 200 }}>
              <InputLabel id="struggling-filter-label">Situation</InputLabel>
              <Select
                labelId="struggling-filter-label"
                value={strugglingFilter}
                onChange={(event) => setStrugglingFilter(event.target.value)}
                label="Situation"
                size="small"
              >
                <MenuItem value="all">All learners</MenuItem>
                <MenuItem value="fine">On track</MenuItem>
                <MenuItem value="struggling">Underperforming</MenuItem>
              </Select>
            </FormControl>
          )}
        </Stack>
      </Box>
      <CustomTable
        entities={filterUsers}
        handleActionClick={handleActionClick}
        fieldToFilter={filterUserByNameOrEmail}
        columns={[
          {
            id: "name",
            label: "Name",
            value: (row: any) => (
              <Stack direction="row" spacing={2} alignItems={"center"}>
                <UserWithPet
                  pictureUrl={row.picture_url}
                  name={row.name}
                  equippedStoreItems={row.equipped_store_items}
                  avatarSize={40}
                />
                <Box>{row.name}</Box>
              </Stack>
            ),
          },
          { id: "email", label: "Email", value: "email" },
          { id: "role", label: "Role", value: "email" },
          ...(showStrugglingColumn
            ? [
                {
                  id: "struggling",
                  label: "Situation",
                  value: (row: User) => (
                    <StrugglingFlagChip
                      summary={strugglingByStudentId.get(row.user_id || "")}
                      orgId={rosterOrgId}
                    />
                  ),
                  sort: (row: User) => {
                    const summary = strugglingByStudentId.get(
                      row.user_id || "",
                    );
                    if (summary?.escalated) return 0;
                    if (summary?.isStruggling) return 1;
                    return 2;
                  },
                },
              ]
            : []),
          {
            id: "organization",
            label: "Organization",
            value: (row: any) => {
              return (
                <Box sx={{ maxWidth: 300, overflow: "hidden" }}>
                  {row.organization?.map((org: any, index: number) => (
                    <Chip
                      key={org.org_id}
                      label={org.name}
                      sx={{ mr: 1, my: 0.5 }}
                    />
                  ))}
                </Box>
              );
            },
          },
          {
            id: "tags",
            label: "Group Tags",
            value: (row: any) => {
              return (
                <Box sx={{ maxWidth: 300, overflow: "hidden" }}>
                  {row.tags.map((tag: any) => (
                    <Chip
                      key={tag.tag.tag_id}
                      label={tag.tag.name}
                      color={TAG_COLOR(tag.tag.type)}
                      sx={{ mr: 1, my: 0.5 }}
                    />
                  ))}
                </Box>
              );
            },
          },
          {
            id: "user_id",
            value: (row: any) => (
              <Stack direction="row" spacing={2} alignItems={"center"}>
                <LinkBase
                  sx={{ cursor: "pointer" }}
                  component={Link}
                  to={`/user/${row.user_id}`}
                >
                  View
                </LinkBase>
              </Stack>
            ),
          },
          { id: "actions", value: "" },
        ]}
        isSelectable={false}
        extraActionItems={[
          { name: TABLE_ACTIONS.EMAIL_INVITE, icon: <Email /> },
          { name: TABLE_ACTIONS.VIEW_FULL_PROFILE, icon: <Person /> },
        ]}
        idColumn={"user_id"}
        sortDefault={SortOrder.asc}
        sortDefaultField={showStrugglingColumn ? "struggling" : "name"}
        allowedMenuItems={[
          TABLE_ACTIONS.EDIT,
          TABLE_ACTIONS.DELETE,
          TABLE_ACTIONS.EMAIL_INVITE,
          TABLE_ACTIONS.VIEW_FULL_PROFILE,
        ]}
        enableCardViewInSmallDevices
        renderCardContent={(data) => {
          return (
            <Box>
              <Stack direction="row" spacing={2} alignItems={"center"} sx={{ minWidth: 0 }}>
                  <UserWithPet
                    pictureUrl={data.picture_url}
                    name={data.name}
                    equippedStoreItems={data.equipped_store_items}
                    avatarSize={40}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body1">{data.name}</Typography>
                    <Typography variant="body2" color="secondary">
                      {data.email}
                    </Typography>
                  </Box>
              </Stack>
              <Box my={1}>
                <Typography variant="body1">
                  <strong>Role:</strong> {data.role}
                </Typography>
              </Box>
              {showStrugglingColumn && (
                <Box my={1}>
                  <Typography variant="body1" component="div">
                    <strong>Situation:</strong>{" "}
                    <StrugglingFlagChip
                      summary={strugglingByStudentId.get(data.user_id || "")}
                      orgId={rosterOrgId}
                    />
                  </Typography>
                </Box>
              )}
              <Box my={1}>
                <Typography variant="body1">
                  <strong>Organization:</strong>{" "}
                </Typography>
                <Box>
                  {data.organization?.map((org: any, index: number) => (
                    <Chip
                      key={index}
                      label={org.name}
                      sx={{ mr: 1, my: 0.5 }}
                    />
                  ))}
                </Box>
              </Box>
              <Box>
                <Typography variant="body1">
                  <strong>Group Tags:</strong>{" "}
                </Typography>
                <Box>
                  {data.tags.map((tag: any) => (
                    <Chip
                      key={tag.tag.tag_id}
                      label={tag.tag.name}
                      color={TAG_COLOR(tag.tag.type)}
                      sx={{ mr: 1, my: 0.5 }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          );
        }}
      />
    </Box>
  );
};
